import { Link } from '@buzola/router'
import {
	ActivityIcon,
	ArrowDownToLineIcon,
	ArrowUpToLineIcon,
	BackpackIcon,
	BriefcaseIcon,
	BuildingIcon,
	ExternalLinkIcon,
	FileTextIcon,
	GraduationCapIcon,
	HandshakeIcon,
	HeartHandshakeIcon,
	InboxIcon,
	KeyRoundIcon,
	LanguagesIcon,
	ListChecksIcon,
	type LucideIcon,
	MessageSquareQuoteIcon,
	MicIcon,
	PanelsTopLeftIcon,
	QuoteIcon,
	SettingsIcon,
	ShieldCheckIcon,
	SignpostIcon,
	SparklesIcon,
	UserRoundCheckIcon,
	UsersIcon,
	UsersRoundIcon,
} from 'lucide-react'
import { AdminOnly } from './admin-only'

type NavRoute =
	| 'pages'
	| 'navigation'
	| 'footer'
	| 'settings'
	| 'redirects'
	| 'languages'
	| 'members'
	| 'api-keys'
	| 'programs'
	| 'faqs'
	| 'testimonials'
	| 'partners'
	| 'community/voice-gatherings'
	| 'community/voice-participants'
	| 'community/hub-members'
	| 'community/mentors'
	| 'community/team'
	| 'community/interns'
	| 'community/alumni-stories'
	| 'community/alumni-jobs'
	| 'community/institutions'
	| 'community/friends'
	| 'community/auspices'
	| 'forms/submissions'
	| 'forms/definitions'
	| 'forms/zoho'

type NavItem = {
	route: NavRoute
	label: string
	icon: LucideIcon
	/** Rendered indented under this item — one level only. */
	children?: NavItem[]
	/** Hidden for non-admin identities (from the members / API-keys work). */
	adminOnly?: boolean
}

type NavSection = {
	title: string
	items: NavItem[]
}

// Sections follow cms-plan/02 (Web · Programmes · Community · Forms · Settings).
// Community carried ten flat rows, which read as one undifferentiated list, so
// the entries that belong to the same programme are nested under their parent.
const sections: NavSection[] = [
	{
		title: 'Web',
		items: [
			{ route: 'pages', label: 'Pages', icon: PanelsTopLeftIcon },
			{ route: 'navigation', label: 'Navigation', icon: ArrowUpToLineIcon },
			{ route: 'footer', label: 'Footer', icon: ArrowDownToLineIcon },
			{ route: 'settings', label: 'Shared site info', icon: SettingsIcon },
		],
	},
	{
		title: 'Programmes',
		items: [
			{ route: 'programs', label: 'Programmes', icon: GraduationCapIcon },
			{ route: 'faqs', label: 'FAQ', icon: MessageSquareQuoteIcon },
			{ route: 'testimonials', label: 'Testimonials', icon: QuoteIcon },
			{ route: 'partners', label: 'Partner companies', icon: HandshakeIcon },
		],
	},
	{
		title: 'Community',
		items: [
			{
				route: 'community/voice-gatherings',
				label: 'Leaders Voice',
				icon: MicIcon,
				children: [
					{ route: 'community/voice-participants', label: 'Participants', icon: UsersRoundIcon },
				],
			},
			{ route: 'community/hub-members', label: 'Leaders Hub', icon: SparklesIcon },
			{ route: 'community/mentors', label: 'Mentors', icon: UserRoundCheckIcon },
			{
				route: 'community/team',
				label: 'ECCEDU team',
				icon: UsersIcon,
				children: [
					{ route: 'community/interns', label: 'ECCEDU interns', icon: BackpackIcon },
				],
			},
			{
				route: 'community/alumni-stories',
				label: 'Alumni stories',
				icon: FileTextIcon,
				children: [
					{ route: 'community/alumni-jobs', label: 'Where they work', icon: BriefcaseIcon },
				],
			},
			{
				route: 'community/institutions',
				label: 'Institutions',
				icon: BuildingIcon,
				children: [
					{ route: 'community/friends', label: 'ECCEDU Friends', icon: HeartHandshakeIcon },
					{ route: 'community/auspices', label: 'Auspices', icon: ShieldCheckIcon },
				],
			},
		],
	},
	{
		title: 'Forms',
		items: [
			{ route: 'forms/submissions', label: 'Form submissions', icon: InboxIcon },
			{ route: 'forms/definitions', label: 'Form settings', icon: ListChecksIcon },
			{ route: 'forms/zoho', label: 'Zoho diagnostics', icon: ActivityIcon },
		],
	},
	{
		title: 'Settings',
		items: [
			{ route: 'redirects', label: 'Redirects', icon: SignpostIcon },
			{ route: 'languages', label: 'Languages', icon: LanguagesIcon },
			{ route: 'members', label: 'Users', icon: UsersIcon, adminOnly: true },
			{ route: 'api-keys', label: 'API keys', icon: KeyRoundIcon, adminOnly: true },
		],
	},
]

const itemClass =
	'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm text-sidebar-foreground/75 transition-colors hover:bg-sidebar-accent hover:text-sidebar-foreground data-active:bg-sidebar-accent data-active:font-medium data-active:text-orange'

function NavLinkRow({ item }: { item: NavItem }) {
	const Icon = item.icon
	return (
		<Link activeExact={false} className={itemClass} to={item.route}>
			<Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
			<span className="truncate">{item.label}</span>
		</Link>
	)
}

/**
 * Left rail — the Obermeyer shape: a light panel separated by a hairline, not a
 * dark slab. Sections are quiet uppercase labels, items are icon + label, and
 * the active item is a filled neutral pill (colour, never a moving indicator).
 */
export function NavSidebar() {
	return (
		<aside className="hidden md:flex md:w-60 lg:w-64 shrink-0 flex-col bg-sidebar text-sidebar-foreground border-r border-sidebar-border sticky top-0 h-screen">
			<div className="px-4 py-4 border-b border-sidebar-border">
				<Link className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-sidebar-accent" to="pages">
					<img alt="" className="h-8 w-8 shrink-0 object-contain" src="/assets/ecce-mark.png" />
					<span className="min-w-0">
						<span className="block truncate text-sm font-semibold leading-tight">ECCEDU</span>
						<span className="block truncate text-xs leading-tight text-sidebar-muted">Administration</span>
					</span>
				</Link>
			</div>

			<nav className="flex-1 overflow-y-auto px-2 py-3">
				{sections.filter(section => section.items.length > 0).map(section => (
					<div className="mb-4 last:mb-0" key={section.title}>
						<div className="px-3 pb-1.5 text-[11px] font-medium uppercase tracking-wider text-sidebar-muted">
							{section.title}
						</div>
						<ul className="space-y-0.5">
							{section.items.map(item => {
								const row = (
									<li key={item.route}>
										<NavLinkRow item={item} />
										{item.children
											? (
												<ul className="ml-[1.4rem] mt-0.5 space-y-0.5 border-l border-sidebar-border pl-2">
													{item.children.map(child => (
														<li key={child.route}>
															<NavLinkRow item={child} />
														</li>
													))}
												</ul>
											)
											: null}
									</li>
								)
								return item.adminOnly ? <AdminOnly fallback={null} key={item.route}>{row}</AdminOnly> : row
							})}
						</ul>
					</div>
				))}
			</nav>

			<footer className="border-t border-sidebar-border px-4 py-3">
				<a
					className="inline-flex items-center gap-1.5 text-xs text-sidebar-muted transition-colors hover:text-orange"
					href="/"
					rel="noreferrer"
					target="_blank"
				>
					<ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
					Go to the website
				</a>
			</footer>
		</aside>
	)
}
